import {HttpErrorResponse} from '@angular/common/http';
import {Component, ElementRef, OnInit, QueryList, ViewChildren} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {QueryParams} from 'src/app/core/interfaces/common';
import {CommonService} from 'src/app/shared/services/common.service';
import {environment} from 'src/environments/environment.generated';
import {Router} from "@angular/router";
import {getAppThemeName} from "../../core/functions/common.function";
import {CommonModule} from '@angular/common';
import {KeepHtmlPipe} from '../../shared/pipes/keep-html.pipe';
import {FormsModule} from '@angular/forms';
import {
  DBIconModule,
  DBSpinnerModule,
  DBPanelModule,
  DBCheckboxModule,
  DBToggleableModule,
  DBAlertModule,
  DBButtonModule
} from '@db/db-core';

@Component({
  selector: 'consent-form-page',
  templateUrl: './consent-form-page.component.html',
  styleUrls: ['./consent-form-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    KeepHtmlPipe,
    FormsModule,
    DBIconModule,
    DBSpinnerModule,
    DBPanelModule,
    DBCheckboxModule,
    DBToggleableModule,
    DBAlertModule,
    DBButtonModule
  ]
})
export class ConsentFormPageComponent implements OnInit {

  @ViewChildren("scopesCheck") scopesCheck!: QueryList<ElementRef>;
  public consentObject: any = '';
  public consentLangObject: any = '';
  public selectAllScopeChecked = false;
  public agreeBtnDisabled = true;
  public isLoading = true;
  public isFormCancelBtnLoading = false;
  public isFormConfirmBtnLoading = false;
  public consentRequestId?: string;

  constructor(
    private readonly appService: CommonService,
    private readonly translate: TranslateService,
    private readonly router: Router
  ) {
  }

  ngOnInit() {
    this.appService.changeShowHeaderAndFooter(true);
    this.getConsentObject();
    this.changeUserLoginStatus();
    this.languageDataSubscription();
  }

  changeUserLoginStatus() {
    this.appService.changeUserLoginStatus(false);
  }

  getConsentObject() {
    const urlExtParams: Array<QueryParams> = [
      {paramName: 'requestTransactionId', paramValue: sessionStorage.getItem('consentRequestId') as string},
      {paramName: 'consentFormData', paramValue: 'consentFormData'}
    ];

    this.appService.getRequestExternal(environment.endPoints.getConsentObject, urlExtParams, []).subscribe({
      next: (data) => {
        this.isLoading = false;
        if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "CONSENT_COLLECTION") {
          const lngList: Array<string> = data.response.content.map((obj: any) => obj.language.toLowerCase());
          if (data.response.content.length === 1) {
            this.appService._langSelectionEnabled.next(false);
          }
          this.consentRequestId = data.response.consentRequestId;
          this.appService.setActiveLang(lngList, data.response.consentTextDefaultLanguage.toLowerCase());
          this.appService.setConsentObject(data.response);
          return;
        }
        if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "BROWSER_REDIRECT_TO_IDP") {
          sessionStorage.setItem('selectedFknKey', '');
          console.log(`Redirecting to ${data.response.consentApprovalRedirectUri} with ${data.response.token} response JWT.`);
          this.appService.postToCIAM(`${data.response.consentApprovalRedirectUri}`, data.response.token);
          return;
        }

        this.router.navigateByUrl('error');
      },
      error: (error) => {

        console.error("Cloud not load the consent agreement texts", error);
        this.router.navigateByUrl('error');
      }
    })
  }

  onSelectAllCheck() {
    this.selectAllScopeChecked = !this.selectAllScopeChecked;
    for (const element of this.consentLangObject.scopes) {
      const index: number = this.consentLangObject.scopes.indexOf(element);
      element.model = this.selectAllScopeChecked;
      if (index === (this.consentLangObject.scopes.length - 1)) {
        this.changeAgreeBtnStatus();
      }
    }
  }

  changeAgreeBtnStatus() {
    let howManyItemsChecked = 0;
    for (const element of this.consentLangObject.scopes) {
      const isAnyElementChecked = element.model;
      if (isAnyElementChecked) {
        howManyItemsChecked++;
      }
    }
    this.agreeBtnDisabled = howManyItemsChecked <= 0;
    this.selectAllScopeChecked = (howManyItemsChecked > 0 && howManyItemsChecked === this.consentLangObject.scopes.length);
  }

  checkScope(pIndex: number) {
    setTimeout(() => {
      this.consentLangObject.scopes[pIndex].model = !this.consentLangObject.scopes[pIndex].model;
      this.changeAgreeBtnStatus();
    }, 200);
  }

  onAgreeConsent() {
    this.isFormConfirmBtnLoading = true;
    const reqData: any = {
      selectedScopes: (this.consentLangObject.scopes.map((scope: any) => {
        if (scope.model) {
          return scope.scopeLabel;
        }
      })).filter((scope: any) => scope !== undefined),
      consentRequestId: this.consentRequestId ?? '',
      displayedFormLanguage: this.consentLangObject.language
    };
    this.appService.postRequestExternal(`${environment.endPoints.postForResource}/${environment.endPoints.postConsent}`, reqData, undefined, undefined, null).subscribe({
      next: (data) => {
        if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "BROWSER_REDIRECT_TO_IDP") {
          sessionStorage.setItem('selectedFknKey', '');
          sessionStorage.removeItem('lang')
          this.appService.changeIDPUserAuth(false);
          console.log(`Redirecting to ${data.response.consentApprovalRedirectUri} with ${data.response.token} response JWT.`);
          this.appService.postToCIAM(`${data.response.consentApprovalRedirectUri}`, data.response.token);
          return;
        }

        this.router.navigateByUrl('error');
      },
      error: (err: HttpErrorResponse) => {
        console.error("Cloud not accept the consent, please try again.", err);
        this.isFormConfirmBtnLoading = false;
        this.router.navigateByUrl('error');
      }
    });
  }

  onCancelConsent() {
    const queryParams: Array<QueryParams> = [{
      paramName: 'consentRequestId',
      paramValue: this.consentRequestId as string
    }];
    this.isFormCancelBtnLoading = true
    this.appService.deleteRequestExternal(environment.endPoints.cancelConsent, queryParams, []).subscribe({
      next: (data) => {
        if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "BROWSER_REDIRECT_TO_IDP") {
          sessionStorage.removeItem('selectedFknKey');
          sessionStorage.removeItem('preselected');
          this.appService.changeIDPUserAuth(false);
          console.log(`Redirecting to ${data.response.consentApprovalRedirectUri} with ${data.response.token} response JWT.`);
          this.appService.postToCIAM(`${data.response.consentApprovalRedirectUri}`, data.response.token);
          console.debug('Consent canceled! With token: ', data);
          return;
        }
        sessionStorage.removeItem('selectedFknKey');
        sessionStorage.removeItem('preselected');
        this.router.navigateByUrl('error');
      },
      error: (errorResponse: HttpErrorResponse) => {
        console.error("Unable to cancel the flow", errorResponse)
        this.isFormCancelBtnLoading = false
        sessionStorage.removeItem('selectedFknKey');
        sessionStorage.removeItem('preselected');
        this.router.navigateByUrl('error');
      }
    });
  }

  replacePlaceholder(getText: any, not_html: any = null) {
    const replaceObj: any = not_html === 'not_html' ? {
      '{PROCESSOR_FULL_NAME}': `${this.consentObject.processorFullName}`,
      '{PROCESSOR_APP_NAME}': `${this.consentObject.processorApplicationName}`,
      '{PROCESSOR_NAME}': `${this.consentObject.processorShortName}`
    } : {
      '{PROCESSOR_FULL_NAME}': `<span style="color: #000000; font-weight: 600;">${this.consentObject.processorFullName}</span>`,
      '{PROCESSOR_APP_NAME}': `<span style="color: #000000; font-weight: 600;">${this.consentObject.processorApplicationName}</span>`,
      '{PROCESSOR_NAME}': `<span style="color: #000000; font-weight: 600;">${this.consentObject.processorShortName}</span>`
    };
    let formattedText = getText;
    for (const key of Object.keys(replaceObj)) {
      if (formattedText) {
        formattedText = formattedText.replaceAll(key, replaceObj[key]);
      }
    }
    return formattedText;
  }

  languageDataSubscription() {
    this.appService._languageData.subscribe(data => {
      this.translate.use(data.activeLang);
      this.appService._consentObject.subscribe(obj => {
        this.consentObject = obj;
        if (obj.content) {
          const getConsentObject = obj.content.find((consent: any) => consent.language.toLowerCase() === data.activeLang.toLowerCase());
          getConsentObject.scopes = getConsentObject.scopes.map((scope: any) => {
            return {...scope, model: false, name: scope.scopeDisplayText};
          });
          getConsentObject.consentTextAddOn = getConsentObject.consentTextAddOn.filter((consentTextAddOn: any) => consentTextAddOn.shorttext !== null && consentTextAddOn.text !== null);
          this.consentLangObject = getConsentObject;
          this.selectAllScopeChecked = false;
          this.changeAgreeBtnStatus();
        }
      });
    });
  }

  getFormStepLabel(label: string) {
    return `NAVIGATION.${getAppThemeName()}.${label}`;
  }

  protected readonly getAppThemeName = getAppThemeName;
  protected readonly sessionStorage = sessionStorage;
}
